import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { formatDayOfYear } from '~/constants';
import { EffectiveDateSource } from '~/models/service/model';
import { fromDateStampObject } from '~/models/time/translator';
import { BillingScheduleGroupType } from '~/types/api/relationship/billingSchedule.model';
import { formatLongMonthDateWithLocalTimezone } from '~/utils/date-formatting';
export const billingScheduleTextsPath = 'billing.billingSchedule';
const formatOrdinal = (day)=>{
    if (day === 1 || day === 21 || day === 31) return "".concat(day, "st");
    if (day === 2 || day === 22) return "".concat(day, "nd");
    if (day === 3 || day === 23) return "".concat(day, "rd");
    return "".concat(day, "th");
};
const formatServiceNotes = (options)=>{
    const { t, service, context = {} } = options;
    const notes = [];
    if (context.isToday && service.priorPeriods && service.priorPeriods > 0 && service.priorPeriodsTotal) {
        notes.push(t.notes.includesPriorPeriods({
            count: service.priorPeriods
        }));
    }
    if (service.isProrated) {
        notes.push(t.notes.firstPaymentProrated());
    }
    if (service.billingStages) {
        notes.push(t.notes.stage({
            stageNumber: service.billingStages.stageNumber,
            totalStages: service.billingStages.totalStages
        }));
    }
    if (service.remainingCharges) {
        notes.push(t.notes.billedTimes({
            count: service.remainingCharges
        }));
    }
    return notes;
};
const translateService = (options)=>{
    var _service_priorPeriods, _service_remainingCharges;
    const { t, service, context } = options;
    return {
        serviceId: service.serviceId,
        name: service.name,
        price: (service.priorPeriodsTotal || service.price) / 100,
        priorPeriods: (_service_priorPeriods = service.priorPeriods) !== null && _service_priorPeriods !== void 0 ? _service_priorPeriods : null,
        remainingCharges: (_service_remainingCharges = service.remainingCharges) !== null && _service_remainingCharges !== void 0 ? _service_remainingCharges : null,
        notes: formatServiceNotes({
            t,
            service,
            context
        })
    };
};
const formatFrequencyLabel = (options)=>{
    const { t, type } = options;
    switch(type){
        case BillingScheduleGroupType.Weekly:
            return t.frequency.weekly();
        case BillingScheduleGroupType.Biweekly:
            return t.frequency.everyTwoWeeks();
        case BillingScheduleGroupType.Monthly:
            return t.frequency.monthly();
        case BillingScheduleGroupType.Quarterly:
            return t.frequency.quarterly();
        case BillingScheduleGroupType.Yearly:
            return t.frequency.yearly();
    }
};
const buildPausedSection = (t, paused, frequencyLabel, groupKey)=>({
        groupKey,
        title: t.sections.billedUponActivation({
            frequency: frequencyLabel
        }),
        totalAmount: paused.totalAmount / 100,
        isPaused: true,
        services: paused.services.map((service)=>translateService({
                t,
                service
            }))
    });
const formatDayOfMonth = (options)=>{
    const { t, dayOfMonth } = options;
    if (!dayOfMonth) return '';
    if (dayOfMonth.type === 'lastDayOfMonth') {
        return t.dayOfMonth.lastDayOfMonth();
    }
    if (dayOfMonth.dayOfMonth) {
        return t.dayOfMonth.onDay({
            ordinal: formatOrdinal(dayOfMonth.dayOfMonth.day)
        });
    }
    return '';
};
const formatQuarterlyDay = (options)=>{
    const { t, billingOnDay, lastDayOfPeriod } = options;
    if (lastDayOfPeriod) {
        return t.quarterlyDay.lastDayOfQuarter();
    }
    if (billingOnDay === 0) {
        return t.quarterlyDay.firstDayOfQuarter();
    }
    return t.quarterlyDay.onDay({
        day: billingOnDay
    });
};
const hasUserSetEffectiveDate = (services)=>services.some((service)=>service.effectiveDateSource === EffectiveDateSource.User);
const effectiveDateKey = (options)=>{
    const { effectiveDate, showStartingDate } = options;
    if (!showStartingDate) return '';
    return "".concat(effectiveDate.year, "-").concat(effectiveDate.month, "-").concat(effectiveDate.day);
};
export const translateBillingSchedule = (options)=>{
    var _billingSchedule_groups;
    const { t, billingSchedule } = options;
    const sections = [];
    const groups = (_billingSchedule_groups = billingSchedule.groups) !== null && _billingSchedule_groups !== void 0 ? _billingSchedule_groups : [];
    for (const group of groups){
        switch(group.type){
            case BillingScheduleGroupType.Today:
                {
                    const services = group.today.services.map((service)=>translateService({
                            t,
                            service,
                            context: {
                                isToday: true
                            }
                        }));
                    sections.push({
                        groupKey: 'today',
                        title: t.sections.billedToday(),
                        totalAmount: group.today.totalAmount / 100,
                        isPaused: false,
                        services
                    });
                    break;
                }
            case BillingScheduleGroupType.OnRelationshipEffectiveDate:
                {
                    const { isFuture } = group.onRelationshipEffectiveDate;
                    if (isFuture) {
                        const services = group.onRelationshipEffectiveDate.services.map((service)=>translateService({
                                t,
                                service,
                                context: {
                                    isEffectiveDate: true
                                }
                            }));
                        sections.push({
                            groupKey: 'effectiveDate',
                            title: t.sections.billedOnDate(),
                            totalAmount: group.onRelationshipEffectiveDate.totalAmount / 100,
                            isPaused: false,
                            services
                        });
                    }
                    break;
                }
            case BillingScheduleGroupType.BilledOnStages:
                {
                    const services = group.billedOnStages.services.map((service)=>translateService({
                            t,
                            service
                        }));
                    sections.push({
                        groupKey: 'billedOnStages',
                        title: t.sections.billedInStages(),
                        totalAmount: group.billedOnStages.totalAmount / 100,
                        isPaused: false,
                        services
                    });
                    break;
                }
            case BillingScheduleGroupType.Weekly:
                {
                    const frequency = formatFrequencyLabel({
                        t,
                        type: group.type
                    });
                    const dayOfWeek = group.weekly.billingOnDayOfWeek;
                    const effectiveDate = fromDateStampObject(group.weekly.effectiveDate);
                    const formattedDate = formatLongMonthDateWithLocalTimezone(effectiveDate);
                    const { isFuture } = group.weekly;
                    const services = group.weekly.services.map((service)=>translateService({
                            t,
                            service
                        }));
                    const showStartingDate = isFuture && hasUserSetEffectiveDate(group.weekly.services);
                    const title = showStartingDate ? t.sections.billedFrequencyOnDayStarting({
                        frequency,
                        day: dayOfWeek,
                        date: formattedDate
                    }) : t.sections.billedFrequencyOnDay({
                        frequency,
                        day: dayOfWeek
                    });
                    sections.push({
                        groupKey: "weekly::".concat(dayOfWeek, "::").concat(String(isFuture), "::").concat(effectiveDateKey({
                            effectiveDate: group.weekly.effectiveDate,
                            showStartingDate
                        })),
                        title,
                        totalAmount: group.weekly.totalAmount / 100,
                        isPaused: false,
                        services
                    });
                    break;
                }
            case BillingScheduleGroupType.Biweekly:
                {
                    const frequency = formatFrequencyLabel({
                        t,
                        type: group.type
                    });
                    const dayOfWeek = group.biweekly.billingOnDayOfWeek;
                    const effectiveDate = fromDateStampObject(group.biweekly.effectiveDate);
                    const formattedDate = formatLongMonthDateWithLocalTimezone(effectiveDate);
                    const { isFuture } = group.biweekly;
                    const services = group.biweekly.services.map((service)=>translateService({
                            t,
                            service
                        }));
                    const showStartingDate = isFuture && hasUserSetEffectiveDate(group.biweekly.services);
                    const title = showStartingDate ? t.sections.billedFrequencyOnDayStarting({
                        frequency,
                        day: dayOfWeek,
                        date: formattedDate
                    }) : t.sections.billedFrequencyOnDay({
                        frequency,
                        day: dayOfWeek
                    });
                    sections.push({
                        groupKey: "biweekly::".concat(dayOfWeek, "::").concat(String(isFuture), "::").concat(effectiveDateKey({
                            effectiveDate: group.biweekly.effectiveDate,
                            showStartingDate
                        })),
                        title,
                        totalAmount: group.biweekly.totalAmount / 100,
                        isPaused: false,
                        services
                    });
                    break;
                }
            case BillingScheduleGroupType.Monthly:
                {
                    var _ref, _ref1;
                    var _group_monthly_dayOfMonth, _group_monthly_dayOfMonth_dayOfMonth, _group_monthly_dayOfMonth1;
                    const frequency = formatFrequencyLabel({
                        t,
                        type: group.type
                    });
                    const dayOfMonthStr = formatDayOfMonth({
                        t,
                        dayOfMonth: group.monthly.dayOfMonth
                    });
                    const effectiveDate = fromDateStampObject(group.monthly.effectiveDate);
                    const formattedDate = formatLongMonthDateWithLocalTimezone(effectiveDate);
                    const { isFuture } = group.monthly;
                    const services = group.monthly.services.map((service)=>translateService({
                            t,
                            service
                        }));
                    const showStartingDate = isFuture && hasUserSetEffectiveDate(group.monthly.services);
                    const title = showStartingDate ? t.sections.billedFrequencyDayStarting({
                        frequency,
                        day: dayOfMonthStr,
                        date: formattedDate
                    }) : t.sections.billedFrequencyDay({
                        frequency,
                        day: dayOfMonthStr
                    });
                    sections.push({
                        groupKey: "monthly::".concat((_ref = (_group_monthly_dayOfMonth = group.monthly.dayOfMonth) === null || _group_monthly_dayOfMonth === void 0 ? void 0 : _group_monthly_dayOfMonth.type) !== null && _ref !== void 0 ? _ref : 'none', "::").concat((_ref1 = (_group_monthly_dayOfMonth1 = group.monthly.dayOfMonth) === null || _group_monthly_dayOfMonth1 === void 0 ? void 0 : (_group_monthly_dayOfMonth_dayOfMonth = _group_monthly_dayOfMonth1.dayOfMonth) === null || _group_monthly_dayOfMonth_dayOfMonth === void 0 ? void 0 : _group_monthly_dayOfMonth_dayOfMonth.day) !== null && _ref1 !== void 0 ? _ref1 : '', "::").concat(String(isFuture), "::").concat(effectiveDateKey({
                            effectiveDate: group.monthly.effectiveDate,
                            showStartingDate
                        })),
                        title: title.trim(),
                        totalAmount: group.monthly.totalAmount / 100,
                        isPaused: false,
                        services
                    });
                    break;
                }
            case BillingScheduleGroupType.Quarterly:
                {
                    const frequency = formatFrequencyLabel({
                        t,
                        type: group.type
                    });
                    const dayStr = formatQuarterlyDay({
                        t,
                        billingOnDay: group.quarterly.billingOnDay,
                        lastDayOfPeriod: group.quarterly.lastDayOfPeriod
                    });
                    const effectiveDate = fromDateStampObject(group.quarterly.effectiveDate);
                    const formattedDate = formatLongMonthDateWithLocalTimezone(effectiveDate);
                    const { isFuture } = group.quarterly;
                    const services = group.quarterly.services.map((service)=>translateService({
                            t,
                            service
                        }));
                    const showStartingDate = isFuture && hasUserSetEffectiveDate(group.quarterly.services);
                    const title = showStartingDate ? t.sections.billedFrequencyDayStarting({
                        frequency,
                        day: dayStr,
                        date: formattedDate
                    }) : t.sections.billedFrequencyDay({
                        frequency,
                        day: dayStr
                    });
                    sections.push({
                        groupKey: "quarterly::".concat(group.quarterly.billingOnDay, "::").concat(String(group.quarterly.lastDayOfPeriod), "::").concat(String(isFuture), "::").concat(effectiveDateKey({
                            effectiveDate: group.quarterly.effectiveDate,
                            showStartingDate
                        })),
                        title: title.trim(),
                        totalAmount: group.quarterly.totalAmount / 100,
                        isPaused: false,
                        services
                    });
                    break;
                }
            case BillingScheduleGroupType.Yearly:
                {
                    const frequency = formatFrequencyLabel({
                        t,
                        type: group.type
                    });
                    const effectiveDate = fromDateStampObject(group.yearly.effectiveDate);
                    const formattedDate = formatLongMonthDateWithLocalTimezone(effectiveDate);
                    const { isFuture, billingDay } = group.yearly;
                    const services = group.yearly.services.map((service)=>translateService({
                            t,
                            service
                        }));
                    const yearlyDayStr = billingDay ? formatDayOfYear(billingDay) : '';
                    const showStartingDate = isFuture && hasUserSetEffectiveDate(group.yearly.services);
                    const title = yearlyDayStr ? showStartingDate ? t.sections.billedFrequencyOnDayStarting({
                        frequency,
                        day: yearlyDayStr,
                        date: formattedDate
                    }) : t.sections.billedFrequencyOnDay({
                        frequency,
                        day: yearlyDayStr
                    }) : showStartingDate ? t.sections.billedFrequencyStarting({
                        frequency,
                        date: formattedDate
                    }) : t.sections.billedFrequency({
                        frequency
                    });
                    sections.push({
                        groupKey: "yearly::".concat(billingDay ? "".concat(billingDay.month, "-").concat(billingDay.day) : 'none', "::").concat(String(isFuture), "::").concat(effectiveDateKey({
                            effectiveDate: group.yearly.effectiveDate,
                            showStartingDate
                        })),
                        title: title.trim(),
                        totalAmount: group.yearly.totalAmount / 100,
                        isPaused: false,
                        services
                    });
                    break;
                }
            case BillingScheduleGroupType.Paused:
                sections.push(buildPausedSection(t, group.paused, t.frequency.monthly(), 'paused::legacy'));
                break;
            case BillingScheduleGroupType.WeeklyPaused:
                sections.push(buildPausedSection(t, group.paused, t.frequency.weekly(), 'paused::weekly'));
                break;
            case BillingScheduleGroupType.BiweeklyPaused:
                sections.push(buildPausedSection(t, group.paused, t.frequency.everyTwoWeeks(), 'paused::biweekly'));
                break;
            case BillingScheduleGroupType.MonthlyPaused:
                sections.push(buildPausedSection(t, group.paused, t.frequency.monthly(), 'paused::monthly'));
                break;
            case BillingScheduleGroupType.QuarterlyPaused:
                sections.push(buildPausedSection(t, group.paused, t.frequency.quarterly(), 'paused::quarterly'));
                break;
            case BillingScheduleGroupType.YearlyPaused:
                sections.push(buildPausedSection(t, group.paused, t.frequency.yearly(), 'paused::yearly'));
                break;
            default:
                {
                    const _exhaustive = group;
                    throw new Error("Unhandled BillingScheduleGroupType: ".concat(_exhaustive.type));
                }
        }
    }
    const combinedSections = new Map();
    const sectionOrder = [];
    for (const section of sections){
        const key = section.groupKey;
        const existing = combinedSections.get(key);
        if (existing) {
            combinedSections.set(key, _object_spread_props(_object_spread({}, existing), {
                totalAmount: existing.totalAmount + section.totalAmount,
                services: [
                    ...existing.services,
                    ...section.services
                ]
            }));
        } else {
            combinedSections.set(key, _object_spread({}, section));
            sectionOrder.push(key);
        }
    }
    const orderedSections = sectionOrder.map((key)=>combinedSections.get(key)).filter((section)=>section !== undefined);
    return {
        sections: orderedSections
    };
};
